import React, { useState, useEffect } from 'react';
import { Plus, Search, Filter, Grid, List, AlertCircle, Crown, Mail } from 'lucide-react';
import RoomCard from './RoomCard';
import CreateRoomModal from './CreateRoomModal';
import UpgradeModal from './UpgradeModal';
import InvitationsModal from './InvitationsModal';
import { useRoomStore } from '../store/roomStore';
import { useAuthStore } from '../store/authStore';
import { useRealtimeRooms } from '../hooks/useRealtimeRooms';
import { useSubscriptionLimits } from '../hooks/useSubscriptionLimits';
import { RoomService } from '../services/roomService';
import { RoomMemberRole } from '../types';
import EmailVerificationBanner from './EmailVerificationBanner';

const RoomsPage: React.FC = () => {
  const { 
    rooms, 
    loading,
    error,
    loadRooms,
    refreshRoomUserProfiles,
    addRoom, 
    updateRoomState, 
    updateRoomSettings,
    updateRoomTimeoutSettings,
    removeUserFromRoom,
    updateUserRole,
    createBroadcastLink,
    updateBroadcastLink,
    revokeBroadcastLink,
    deleteBroadcastLink,
    updateRoomStates,
    leaveRoom
  } = useRoomStore();
  const { user } = useAuthStore();
  const { currentPlan, planLimits, checkLimit } = useSubscriptionLimits();
  useRealtimeRooms(); // Set up real-time subscriptions

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showInvitationsModal, setShowInvitationsModal] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'available' | 'occupied' | 'custom'>('all');
  const [sortBy, setSortBy] = useState<'name' | 'updated' | 'status'>('name');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [pendingInvitations, setPendingInvitations] = useState(0);

  useEffect(() => {
    if (user) {
      loadRooms(user.id);
      loadPendingInvitations();
    }
  }, [user, loadRooms]);

  const loadPendingInvitations = async () => {
    if (!user) return;
    
    try {
      const invitations = await RoomService.getUserInvitations(user.id);
      setPendingInvitations(invitations.length);
    } catch (error) {
      console.error('Failed to load pending invitations:', error);
    }
  };

  const handleCreateRoom = async () => {
    if (!user) return;

    // Check if user can create more rooms
    const { allowed, reason } = await checkLimit('rooms', rooms.length);
    
    if (!allowed) {
      setUpgradeReason(reason || 'You have reached your room limit.');
      setShowUpgradeModal(true);
      return;
    }

    setIsCreateModalOpen(true);
  };

  const handleInviteUser = async (roomId: string, email: string, role: RoomMemberRole, message?: string) => {
    try {
      const room = rooms.find(r => r.id === roomId);
      if (!room) return;

      // Check participant limit
      const { allowed, reason } = await checkLimit('participants', room.users.length);
      
      if (!allowed) {
        setUpgradeReason(reason || 'You have reached the participant limit for this room.');
        setShowUpgradeModal(true);
        return;
      }

      await RoomService.inviteUserToRoom(roomId, email, role, message);
      
      // Reload rooms to get updated data
      if (user) {
        loadRooms(user.id);
      }
    } catch (error) {
      console.error('Failed to invite user:', error);
      throw error;
    }
  };

  const handleCreateBroadcastLink = async (roomId: string, options?: any) => {
    const room = rooms.find(r => r.id === roomId);
    if (!room) return;

    // Check broadcast link limit
    const { allowed, reason } = await checkLimit('broadcastLinks', room.broadcastLinks.length);
    
    if (!allowed) {
      setUpgradeReason(reason || 'You have reached the broadcast link limit for this room.');
      setShowUpgradeModal(true);
      return;
    }

    await createBroadcastLink(roomId, options);
  };

  const handleUpdateRoomStates = async (roomId: string, states: any[]) => {
    try {
      await updateRoomStates(roomId, states);
    } catch (error) {
      console.error('Failed to update room states:', error);
      throw error;
    }
  };

  const handleRefreshRoomUserProfiles = (roomId: string) => {
    return refreshRoomUserProfiles(roomId);
  };

  const handleLeaveRoom = async (roomId: string) => {
    if (!user) return;
    try {
      await leaveRoom(roomId, user.id);
    } catch (error) {
      console.error('Failed to leave room:', error);
      throw error;
    }
  };

  // Filter and sort rooms
  const filteredAndSortedRooms = React.useMemo(() => {
    let filtered = rooms.filter(room => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!room.name.toLowerCase().includes(query) && 
            !room.currentState.name.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== 'all') {
        if (filterStatus === 'available' && !room.currentState.name.toLowerCase().includes('available')) {
          return false;
        }
        if (filterStatus === 'occupied' && !room.currentState.name.toLowerCase().includes('occupied')) {
          return false;
        }
        if (filterStatus === 'custom' && room.states.length <= 2) {
          return false;
        }
      }

      return true;
    });

    // Sort rooms
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'updated':
          return b.lastUpdated - a.lastUpdated;
        case 'status':
          return a.currentState.name.localeCompare(b.currentState.name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [rooms, searchQuery, filterStatus, sortBy]);

  if (loading && rooms.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-blue-200 dark:border-blue-800 border-t-blue-600 dark:border-t-blue-400 mx-auto mb-4 sm:mb-6"></div>
            <div className="absolute inset-0 rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-transparent border-t-purple-400 dark:border-t-purple-600 animate-spin mx-auto" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
          </div>
          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg font-medium">Loading your rooms...</p>
          <p className="text-slate-400 dark:text-slate-500 text-sm mt-2">Setting up your workspace</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-all duration-300">
      {/* Email Verification Banner */}
      <EmailVerificationBanner />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-200">
                  Your Rooms
                </h1>
                {planLimits && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full text-xs font-medium">
                    <Crown className="w-3 h-3" />
                    {currentPlan === 'free' ? 'Free' : currentPlan === 'pro' ? 'Pro' : 'Enterprise'}
                    {planLimits.maxRooms !== -1 && (
                      <span className="opacity-80">
                        ({rooms.length}/{planLimits.maxRooms})
                      </span>
                    )}
                  </div>
                )}
              </div>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Manage and monitor all your room states in real-time
              </p>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Invitations Button */}
              <button
                onClick={() => setShowInvitationsModal(true)}
                className="relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
              >
                <Mail className="w-4 h-4" />
                Invitations
                {pendingInvitations > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {pendingInvitations}
                  </span>
                )}
              </button>

              {user?.role === 'ADMIN' && (
                <button
                  onClick={handleCreateRoom}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium"
                >
                  <Plus className="w-4 h-4" />
                  New Room
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Plan Limits Warning */}
        {planLimits && currentPlan === 'free' && rooms.length >= planLimits.maxRooms - 1 && (
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-amber-800 dark:text-amber-300">
                  Approaching Room Limit
                </h3>
                <p className="text-amber-700 dark:text-amber-400 text-sm mt-1">
                  You're using {rooms.length} of {planLimits.maxRooms} rooms in your Free plan. 
                  Upgrade to Pro for up to 10 rooms or Enterprise for unlimited rooms.
                </p>
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="mt-2 text-sm font-medium text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:hover:text-amber-200 underline"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters and Search */}
        <div className="mb-6 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search rooms..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-3 w-full text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent bg-white/80 dark:bg-slate-700/80 backdrop-blur-sm text-slate-900 dark:text-slate-100 transition-all duration-200"
            />
          </div>
          
          {/* Filters and View Controls */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white/80 dark:bg-slate-700/80 backdrop-blur-sm text-slate-900 dark:text-slate-100 transition-all duration-200"
              >
                <option value="all">All Rooms</option>
                <option value="available">Available</option>
                <option value="occupied">Occupied</option>
                <option value="custom">Custom States</option>
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="px-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-white/80 dark:bg-slate-700/80 backdrop-blur-sm text-slate-900 dark:text-slate-100 transition-all duration-200"
              >
                <option value="name">Sort by Name</option>
                <option value="updated">Sort by Updated</option>
                <option value="status">Sort by Status</option>
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white/80 dark:bg-slate-700/80 backdrop-blur-sm rounded-xl p-1 border border-slate-200 dark:border-slate-600">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-500 text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600'
                }`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-500 text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-600'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50/80 dark:bg-red-900/20 backdrop-blur-sm border border-red-200/50 dark:border-red-700/50 rounded-xl flex items-start gap-3 shadow-sm">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-red-800 dark:text-red-300 font-medium text-sm sm:text-base">Something went wrong</p>
              <p className="text-red-600 dark:text-red-400 text-xs sm:text-sm mt-1 break-words">{error}</p>
            </div>
          </div>
        )}

        {/* Rooms Grid/List */}
        {filteredAndSortedRooms.length === 0 && !loading ? (
          <div className="text-center py-12 sm:py-20 px-4">
            <div className="relative mb-6 sm:mb-8">
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 to-purple-900/30 rounded-2xl sm:rounded-3xl mx-auto flex items-center justify-center shadow-lg">
                {searchQuery || filterStatus !== 'all' ? (
                  <Search className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400" />
                ) : (
                  <Plus className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400" />
                )}
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2 sm:mb-3">
              {searchQuery || filterStatus !== 'all' ? 'No rooms found' : 'No rooms yet'}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
              {searchQuery || filterStatus !== 'all' 
                ? 'Try adjusting your search or filters to find what you\'re looking for'
                : 'Create your first room to start managing states and collaborating with your team'
              }
            </p>
            {(searchQuery || filterStatus !== 'all') ? (
              <button
                onClick={() => {
                  setSearchQuery('');
                  setFilterStatus('all');
                  setSortBy('name');
                }}
                className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-slate-500 to-slate-600 text-white rounded-xl sm:rounded-2xl hover:from-slate-600 hover:to-slate-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-sm sm:text-lg font-semibold"
              >
                <Filter className="w-5 h-5 sm:w-6 sm:h-6" />
                <span>Clear Filters</span>
              </button>
            ) : user?.role === 'ADMIN' && (
              <button
                onClick={handleCreateRoom}
                className="inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl sm:rounded-2xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-sm sm:text-lg font-semibold"
              >
                <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
                <span>Create Your First Room</span>
              </button>
            )}
          </div>
        ) : (
          <div className={
            viewMode === 'grid' 
              ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
              : "space-y-4"
          }>
            {filteredAndSortedRooms.map((room, index) => (
              <div
                key={room.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <RoomCard
                  room={room}
                  currentUser={user!}
                  onStateChange={(stateId) => updateRoomState(room.id, stateId, user!.id)}
                  onSettingsChange={(settings) => updateRoomSettings(room.id, settings)}
                  onTimeoutSettingsChange={(settings) => updateRoomTimeoutSettings(room.id, settings)}
                  onInviteUser={(email, role, message) => handleInviteUser(room.id, email, role, message)}
                  onRemoveUser={(userId) => removeUserFromRoom(room.id, userId)}
                  onUpdateUserRole={(userId, role) => updateUserRole(room.id, userId, role)}
                  onCreateBroadcastLink={(options) => handleCreateBroadcastLink(room.id, options)}
                  onUpdateBroadcastLink={(linkId, updates) => updateBroadcastLink(room.id, linkId, updates)}
                  onRevokeBroadcastLink={(linkId) => revokeBroadcastLink(room.id, linkId)}
                  onDeleteBroadcastLink={(linkId) => deleteBroadcastLink(room.id, linkId)}
                  onUpdateRoomStates={(states) => handleUpdateRoomStates(room.id, states)}
                  onRefreshUserProfiles={() => handleRefreshRoomUserProfiles(room.id)}
                  onLeaveRoom={() => handleLeaveRoom(room.id)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateRoomModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={(roomData) => {
          addRoom({
            ...roomData,
            users: [user!]
          }, user!.id);
        }}
      />

      <InvitationsModal
        isOpen={showInvitationsModal}
        onClose={() => {
          setShowInvitationsModal(false);
          loadPendingInvitations(); // Refresh count when modal closes
        }}
      />

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        reason={upgradeReason}
        currentPlan={currentPlan}
        suggestedPlan={currentPlan === 'free' ? 'pro' : 'enterprise'}
      />
    </div>
  );
};

export default RoomsPage;